import React, { useCallback, useMemo } from 'react';
import isEqual from 'fast-deep-equal';
import PropTypes from 'prop-types';

import ChartPlaceholderWrapper from './ChartPlaceholderWrapper';

const usePassThrough = (callback, id) => {
  return useCallback(
    (...args) => {
      callback(id, ...args);
    },
    [id, callback]
  );
};

const Chart = React.memo(
  ({
    config,
    dataPanelsRows,
    dataRevision,
    isFullscreen,
    isPendingRendering,
    onDataSourceChange,
    onExitFullscreen,
    onFinishRender,
    onFullscreen,
    onPanelChange,
    onUpdateDataPanelRows,
    onVisibilityChange,
    visibilityDebounceDelay,
    panelSharedConfig
  }) => {
    const id = config.chartId;

    const handleDataSourceChange = usePassThrough(onDataSourceChange, id);
    const handleFinishRender = usePassThrough(onFinishRender, id);
    const handlePanelChange = usePassThrough(onPanelChange, id);
    const handleVisibilityChange = usePassThrough(onVisibilityChange, id);
    const handleUpdateDataPanelRows = usePassThrough(onUpdateDataPanelRows, id);

    const ChartProps = useMemo(() => {
      return {
        config,
        dataRevision,
        enableDataRevision: true,
        isFullscreen,
        isPendingRendering,
        onDataSourceChange: handleDataSourceChange,
        onExitFullscreen,
        onFinishRender: handleFinishRender,
        onFullscreen,
        panelSharedConfig
      };
    }, [
      config,
      dataRevision,
      handleDataSourceChange,
      handleFinishRender,
      isFullscreen,
      onExitFullscreen,
      onFullscreen,
      isPendingRendering,
      panelSharedConfig
    ]);

    return (
      <ChartPlaceholderWrapper
        dataPanelsRows={dataPanelsRows}
        onPanelChange={handlePanelChange}
        onUpdateDataPanelRows={handleUpdateDataPanelRows}
        onVisibilityChange={handleVisibilityChange}
        visibilityDebounceDelay={visibilityDebounceDelay}
        ChartProps={ChartProps}
      />
    );
  },
  isEqual
);

Chart.displayName = 'Chart';

Chart.propTypes = {
  config: PropTypes.object.isRequired,
  dataRevision: PropTypes.number.isRequired,
  isFullscreen: PropTypes.bool.isRequired,
  isPendingRendering: PropTypes.bool.isRequired,
  onDataSourceChange: PropTypes.func.isRequired,
  onExitFullscreen: PropTypes.func.isRequired,
  onFinishRender: PropTypes.func.isRequired,
  onFullscreen: PropTypes.func.isRequired,
  onPanelChange: PropTypes.func.isRequired,
  onUpdateDataPanelRows: PropTypes.func.isRequired,
  onVisibilityChange: PropTypes.func.isRequired,
  visibilityDebounceDelay: PropTypes.number.isRequired,
  panelSharedConfig: PropTypes.object
};

export default Chart;
